/**
 * Works Section
 * Project showcase with categorized sections
 */

// Project data structure
export const projectsData = {
  websites: [
    {
      name: "Ministry Of Fun",
      headline: "A vibrant digital presence built around brand personality and engagement",
      description: "The business required a vibrant and engaging online presence to reflect its brand identity and attract new customers. A visually engaging website was designed and developed to showcase services, communicate brand personality, and provide a seamless browsing experience.",
      outcomes: [
        "Strengthened brand presence online",
        "Improved customer engagement",
        "Clear presentation of services",
        "Enhanced credibility with potential clients"
      ],
      tags: ["Website", "Brand Presence"],
      screenshot: "/works-covers/Ministry Of Fun Cover.webp",
      ctaType: "view-app",
      ctaLink: "https://ministryoffun.co.za",
      websiteUrl: "https://ministryoffun.co.za"
    },
    {
      name: "Taj Printers",
      headline: "Professional print services with a strong digital and eCommerce presence",
      description: "The client needed a professional digital presence to showcase printing services and attract business clients. A clean and structured website and eCommerce store was built to present services, highlight offerings, and provide clear contact pathways.",
      outcomes: [
        "Improved visibility of services",
        "Increased credibility for B2B clients",
        "Streamlined customer inquiries",
        "Professional online representation"
      ],
      tags: ["Website", "eCommerce", "B2B"],
      screenshot: "/works-covers/Taj Printers Cover.webp",
      ctaType: "view-app",
      ctaLink: "https://tajprinters.co.za/",
      websiteUrl: "https://tajprinters.co.za/"
    },
    {
      name: "UtilityHut",
      headline: "A modern headless eCommerce store built for clarity and accessibility",
      description: "The business required a clean modern online store that doesn't feel like every other, yet remains effective in communicating its offerings and improving online accessibility. A modern headless eCommerce store was developed with clear navigation, structured content, and responsive design.",
      outcomes: [
        "Improved online accessibility",
        "Clear communication of services",
        "Enhanced user experience",
        "Better customer engagement"
      ],
      tags: ["Website", "eCommerce"],
      screenshot: "/works-covers/Utility Hut Cover.webp",
      ctaType: "view-app",
      ctaLink: "https://www.utilityhut.co.za",
      websiteUrl: "https://www.utilityhut.co.za"
    },
    {
      name: "BrightMindsLearn",
      headline: "Helping learners discover their potential through a structured digital presence",
      description: "An educational platform required a digital presence to communicate its value and reach a wider audience. A structured and user-friendly website was developed to present programmes, engage users, and support growth — helping Grades 3–7 learners in Johannesburg and across South Africa discover their full potential through interactive, confidence-building lessons.",
      outcomes: [
        "Improved accessibility to educational content",
        "Increased user engagement",
        "Clear communication of offerings",
        "Scalable platform for future growth"
      ],
      tags: ["Website", "Education"],
      screenshot: "/works-covers/BrightMinds Learn Cover.webp",
      ctaType: "view-app",
      ctaLink: "https://brightmindslearn.co.za",
      websiteUrl: "https://brightmindslearn.co.za"
    },
    {
      name: "S4Techno",
      headline: "A clean, professional presence that positions technical capability with confidence",
      description: "The company needed a modern website to reflect its technical capabilities and build trust with clients. A clean, professional website was developed to highlight services and position the company as a reliable technology provider.",
      outcomes: [
        "Strengthened digital presence",
        "Improved brand perception",
        "Clear service communication",
        "Increased client trust"
      ],
      tags: ["Website", "Technology"],
      screenshot: "/works-covers/S4 Techno Cover.webp",
      ctaType: "view-app",
      ctaLink: "https://s4techno.co.za",
      websiteUrl: "https://s4techno.co.za"
    },
    {
      name: "ThinkinCode",
      headline: "Our own digital home — built to the same standard we hold every project to",
      description: "A strong digital presence was required to represent ThinkinCode's capabilities, projects, and positioning as a systems-focused company. A modern, structured website was developed to showcase work, communicate services, and guide users through the brand's offerings.",
      outcomes: [
        "Clear positioning as a systems and solutions company",
        "Improved presentation of work and projects",
        "Stronger brand identity",
        "Enhanced user experience"
      ],
      tags: ["Website", "Brand"],
      screenshot: "/works-covers/ThinkinCode Cover.webp",
      ctaType: "view-app",
      ctaLink: "/"
    },
    {
      name: "Ayesha's Real Estate Landing Page",
      headline: "A conversion-focused landing page built to capture leads and showcase listings",
      description: "The client needed a focused landing page to capture leads and promote property listings effectively, for a competition being run by the agency. A conversion-focused landing page was developed with clear messaging, strong visuals, and lead capture elements.",
      outcomes: [
        "Improved lead generation",
        "Clear call-to-action flow",
        "Focused user journey",
        "Increased conversion potential"
      ],
      tags: ["Landing Page", "Real Estate"],
      screenshot: "/works-covers/Ayeshas Real Estate Cover.webp",
      ctaType: "view-app",
      ctaLink: "/#contact"
    },
    {
      name: "Paper Architects",
      headline: "A minimal, portfolio-first website that lets the architecture speak for itself",
      description: "The business required a minimal and professional website to showcase architectural work and attract clients. A clean, visually driven website was developed to highlight projects and communicate expertise through refined design and strong visual storytelling.",
      outcomes: [
        "Improved portfolio presentation",
        "Enhanced professional image",
        "Better client engagement",
        "Clear project showcasing"
      ],
      tags: ["Website", "Portfolio"],
      screenshot: "/works-covers/Paper Architects Cover.webp",
      ctaType: "view-app",
      ctaLink: "/#contact"
    },
    {
      name: "M-Power",
      headline: "A modern website built to communicate services and support business growth",
      description: "The company needed a strong digital presence to communicate services and support business growth. A modern website was built with structured content and clear navigation to improve user experience and drive meaningful engagement.",
      outcomes: [
        "Improved online presence",
        "Better communication of services",
        "Increased credibility",
        "Enhanced user engagement"
      ],
      tags: ["Website"],
      screenshot: "/works-covers/M Power Cover.webp",
      ctaType: "view-app",
      ctaLink: "https://mpowerops.netlify.app",
      websiteUrl: "https://mpowerops.netlify.app"
    }
  ],
  businessSolutions: [
    {
      name: "Trendsetter Carwash System",
      headline: "Complete operational control for multi-branch carwash businesses",
      description: "The client required a complete operational system to manage a multi-branch carwash, including customer tracking, payments, workflow status, and reporting. Existing processes were manual, fragmented, and difficult to scale across branches. A full-stack carwash management platform was developed, combining POS functionality with real-time workflow tracking. The system manages vehicles through different service stages, tracks payments, supports customer profiles, and provides reporting dashboards for daily operations and financial visibility.",
      outcomes: [
        "Centralised all carwash operations into a single system",
        "Reduced manual tracking and operational errors",
        "Improved visibility of daily revenue and workflow",
        "Enabled scalability across multiple branches",
        "Enhanced customer tracking and repeat business"
      ],
      tags: ["SaaS", "POS System", "Operations Management"],
      screenshot: "/works-covers/Car Wash OS.webp",
      ctaType: "request-demo",
      ctaLink: "/#contact"
    },
    {
      name: "ThinkinLink",
      headline: "Centralised communication management built for SLA compliance and great service",
      description: "Businesses required a structured way to manage communication and streamline interactions across platforms, managing support tickets and client communication in order to maintain great customer service experiences and SLAs agreed upon. A system was developed to centralise communication and improve interaction efficiency across platforms.",
      outcomes: [
        "Improved communication flow",
        "Reduced fragmentation across platforms",
        "Increased operational efficiency",
        "Scalable communication system"
      ],
      tags: ["SaaS", "Communication"],
      screenshot: "/works-covers/ThinkinLink Cover.webp",
      ctaType: "request-demo",
      ctaLink: "/#contact"
    },
    {
      name: "Leisure Breaks Platform",
      headline: "A fully customised property portfolio and booking management platform",
      description: "The business needed a platform to manage listings, bookings, and property information efficiently. A fully customised, structured Property Portfolio Management system was developed to manage listings and streamline the booking experience for guests and staff alike.",
      outcomes: [
        "Improved listing management",
        "Simplified booking processes",
        "Enhanced user experience",
        "Scalable platform for growth"
      ],
      tags: ["SaaS", "Booking Platform"],
      screenshot: "/works-covers/Leisure Breaks Site Cover.webp",
      ctaType: "request-demo",
      ctaLink: "https://leisurebreaks.co.za"
    },
    {
      name: "Business AI Agent Chatbots",
      headline: "Custom AI agents that handle customer queries and scale engagement 24/7",
      description: "Businesses needed a way to handle customer queries, automate responses, and improve engagement without increasing staff workload. Custom AI-driven chatbot systems were developed to handle customer interactions, automate responses, and integrate with business workflows such as bookings or inquiries.",
      outcomes: [
        "Reduced manual customer support workload",
        "Improved response times",
        "Enabled 24/7 customer interaction",
        "Increased operational efficiency",
        "Scalable across multiple business use cases"
      ],
      tags: ["AI", "Automation", "Customer Engagement"],
      screenshot: "/works-covers/Business AI Agent Chatbots Cover.webp",
      ctaType: "request-demo",
      ctaLink: "/#contact"
    },
    {
      name: "Fleet Management by ThinkinCode",
      headline: "A mobile-first platform built to track deliveries, manage fleets, and reduce delays",
      description: "Businesses required a way to manage deliveries, track drivers, and optimise routing without relying on large-scale enterprise logistics systems. A mobile-first logistics platform was built to track deliveries, manage fleet movement, and monitor driver activity. The system integrates route tracking, delivery status updates, and scheduling tools to streamline operations.",
      outcomes: [
        "Improved delivery tracking accuracy",
        "Reduced delays through better route visibility",
        "Enabled real-time fleet monitoring",
        "Simplified coordination between admin and drivers",
        "Scalable solution for growing operations"
      ],
      tags: ["SaaS", "Logistics", "Fleet Management"],
      screenshot: "/works-covers/Logistics Application (Delivery + Fleet Management Tracking) Cover.webp",
      ctaType: "request-demo",
      ctaLink: "/#contact"
    },
    {
      name: "PropertyTrackr",
      headline: "Centralised property management built for agents, landlords, and administrators",
      description: "Property managers and Estate Agents needed a structured way to manage tenants, payments, and property data without relying on spreadsheets or disconnected tools. A property management platform was developed to centralise tenant records, rental tracking, and property data. The system allows administrators to monitor payments, manage units, and maintain structured records.",
      outcomes: [
        "Centralised tenant and property data",
        "Simplified rent tracking and management",
        "Reduced administrative workload",
        "Improved organisation and reporting",
        "Scalable for multiple properties"
      ],
      tags: ["SaaS", "Property Management"],
      screenshot: "/works-covers/PropertyTrackr Cover 2.webp",
      carouselImages: [
        "/works-covers/PropertyTrackr Cover 2.webp",
        "/works-covers/PropertyTrackr Cover.webp"
      ],
      ctaType: "request-demo",
      ctaLink: "/#contact"
    },
    {
      name: "NotifyS",
      headline: "A reliable notification system that automates communication delivery at scale",
      description: "Businesses required a reliable way to send notifications and updates to customers or users. A notification system was developed to manage and automate communication delivery across multiple channels.",
      outcomes: [
        "Improved communication efficiency",
        "Automated notifications",
        "Reduced manual messaging",
        "Scalable communication tool"
      ],
      tags: ["SaaS", "Notifications"],
      screenshot: "/works-covers/NotifyS Cover.webp",
      ctaType: "request-demo",
      ctaLink: "/#contact"
    },
    {
      name: "StickrLab",
      headline: "A streamlined platform for product customisation and order workflow management",
      description: "The business needed a platform to manage product customisation and ordering processes. A system was developed to handle product configuration and streamline order workflows, reducing manual overhead and improving the customer ordering experience.",
      outcomes: [
        "Simplified ordering process",
        "Improved user interaction",
        "Reduced manual processing",
        "Enhanced operational efficiency"
      ],
      tags: ["SaaS", "E-commerce"],
      screenshot: "/works-covers/StickrLab Cover.webp",
      ctaType: "request-demo",
      ctaLink: "/#contact"
    },
    {
      name: "Sales KPI Performance Tracker",
      headline: "Clear visibility into sales performance with data that drives better decisions",
      description: "Businesses needed a clear way to track sales performance and monitor key metrics. A tracking system was developed to visualise and manage sales performance data, improving accountability and supporting data-driven strategy.",
      outcomes: [
        "Improved visibility of performance metrics",
        "Better decision-making",
        "Simplified reporting",
        "Increased accountability"
      ],
      tags: ["SaaS", "Analytics"],
      screenshot: "/works-covers/KPI Trackr Cover.webp",
      ctaType: "request-demo",
      ctaLink: "/#contact"
    },
    {
      name: "TasksByThinkinCode",
      headline: "Minute-level task tracking built for team productivity and billable hour management",
      description: "Teams required a structured way to manage tasks, efficiency of staff, billable hours, and workflows across operations. A task management system was developed to organise work, track time at a granular level, and improve overall productivity.",
      outcomes: [
        "Improved task tracking, by the minute",
        "Increased team productivity",
        "Clear workflow management",
        "Reduced operational confusion"
      ],
      tags: ["SaaS", "Productivity"],
      screenshot: "/works-covers/TasksByThinkinCode Cover.webp",
      ctaType: "request-demo",
      ctaLink: "https://tasks.thinkincode.co.za/"
    },
    {
      name: "WIP Tracker",
      headline: "Real-time work-in-progress visibility with automated reporting built in",
      description: "Businesses needed visibility into ongoing work and progress across tasks or projects. A tracking system was built to monitor work-in-progress and improve operational oversight, with efficient automated reporting to reduce manual status updates.",
      outcomes: [
        "Increased visibility of work status",
        "Improved operational control",
        "Reduced delays",
        "Better workflow tracking"
      ],
      tags: ["SaaS", "Operations"],
      screenshot: "/works-covers/WIP Tracker Cover.webp",
      ctaType: "request-demo",
      ctaLink: "/#contact"
    },
    {
      name: "KlicnGo",
      headline: "Automated order and interaction management across multiple eCommerce channels",
      description: "The platform required a system to streamline user interactions and improve accessibility of services across multiple eCommerce sites. A digital platform was developed to enhance usability and manage interactions efficiently, automating the management of online orders and securing clients who opt to collect in-store via custom tools developed to prevent fraud and theft.",
      outcomes: [
        "Improved user experience",
        "Simplified interaction flow",
        "Reduced administration",
        "Secure methods to prevent fraud and theft",
        "Automated workflows running 24/7",
        "Enhanced accessibility",
        "Scalable system architecture"
      ],
      tags: ["SaaS"],
      screenshot: "/works-covers/KlicnGo Cover.webp",
      ctaType: "request-demo",
      ctaLink: "/#contact"
    },
    {
      name: "ERP Repair Management System",
      headline: "A structured ERP system built to manage repair workflows end-to-end",
      description: "Repair-based businesses required a structured system to manage jobs, track progress, and handle customer data. An ERP-style system was developed to manage repair workflows, customer information, and operational processes from intake to completion.",
      outcomes: [
        "Centralised repair management",
        "Improved workflow tracking",
        "Reduced manual processes",
        "Enhanced operational efficiency"
      ],
      tags: ["ERP", "SaaS"],
      screenshot: "/works-covers/ERP CMS App Cover.webp",
      ctaType: "request-demo",
      ctaLink: "/#contact"
    }
  ],
  sportsPlatforms: [
    {
      name: "OffCourt",
      headline: "A structured padel league platform built for match management and player engagement",
      description: "Padel league organisers required a central platform to manage matches, track results, and create structured competition environments. A sports platform was developed to manage matches, track results, and create a more engaging experience for players. The system supports structured play and performance tracking as well as live scoring and player metrics tracking.",
      blogMarkdown: `# Padel League Management & Live Scoring Software in South Africa

Running a padel league with spreadsheets, WhatsApp messages, and manual score entry becomes chaotic as more players join. Fixtures change, results come in late, and standings are hard to keep accurate.

ThinkinCode provides a digital padel league management solution designed to simplify match scheduling, automate standings, and deliver real-time updates for players and organisers.

---

## The Problem with Traditional League Management

Most clubs still rely on:
- Spreadsheets for fixtures and standings
- WhatsApp groups for results and announcements
- Manual score reporting (often delayed)
- No single source of truth for league tables

This leads to:
- Confusion and disputes about results
- Admin overload for organisers
- Inaccurate tables and slow updates
- Lower player engagement

---

## The Solution: A Digital Padel League Platform

A league platform like OffCourt digitises the entire season flow.

With ThinkinCode’s system, clubs can:
- Manage fixtures and rounds in one place
- Record results quickly and consistently
- Update standings automatically
- Track player performance and match history
- Keep players engaged with real-time visibility

---

## Features to Look For in Padel League Software

- Fast match result capture  
- Automatic standings + leaderboards  
- Season / division support  
- Player profiles and performance tracking  
- Mobile-friendly UI for on-court use  
- Notifications and updates for players  

---

## Conclusion

Padel leagues grow fast, and manual processes don’t scale. A digital padel league management platform reduces admin work, improves accuracy, and keeps players engaged throughout the season.`,
      outcomes: [
        "Improved match organisation",
        "Enabled structured competition tracking",
        "Increased player engagement",
        "Created a central platform for padel activity",
        "Scalable for clubs and leagues"
      ],
      tags: ["Sports Tech", "SaaS"],
      screenshot: "/works-covers/OffCourt Cover.webp",
      ctaType: "view-app",
      ctaLink: "https://offcourtpadel.thinkincode.co.za"
    },
    {
      name: "Touchline",
      headline: "Social football leagues managed end-to-end with less admin and more structure",
      description: "Social football leagues needed a way to organise fixtures, track scores, and manage teams efficiently. A football league management system was developed to handle fixtures, team structures, and results tracking, improving the overall organisation of matches.",
      outcomes: [
        "Simplified league management",
        "Centralised fixtures and results",
        "Improved player engagement",
        "Reduced admin workload",
        "Enhanced league professionalism"
      ],
      tags: ["Sports Tech", "League Management"],
      screenshot: "/works-covers/Touchline Cover.webp",
      ctaType: "request-demo",
      ctaLink: "/#contact"
    },
    {
      name: "PadelEngine",
      headline: "Tournament and player management for padel clubs built around professionalism",
      description: "Padel clubs required a modern system to manage tournaments, players, and match tracking while creating a professional experience. A tournament and player management platform was developed for padel clubs, enabling match tracking, scheduling, and player engagement.",
      blogMarkdown: `# Padel Tournament Scoring & Management Software in South Africa

Managing padel tournaments manually is slow, error-prone, and difficult to scale. Modern padel clubs are now using digital scoring and tournament management software to track matches, update live scores, and improve the overall player and spectator experience.

ThinkinCode provides a digital padel tournament management solution designed to simplify scoring, automate tournament draws, and deliver real-time match updates.

---

## The Problem with Traditional Padel Tournament Management

Most padel clubs still rely on manual systems such as:
- Paper-based score tracking
- WhatsApp updates for match results
- Spreadsheets for tournament draws
- Manual bracket updates

This leads to:
- Delayed score updates
- Disputes between players
- Admin overload for organisers
- Poor spectator experience
- Lack of real-time visibility

As tournaments grow, these problems become harder to manage.

---

## The Solution: Digital Padel Tournament Management Software

A padel tournament scoring app solves these issues by digitising the entire tournament process.

With ThinkinCode’s system, clubs can:
- Manage tournament draws automatically
- Record live stats and match scores in real-time
- Track player progress through brackets
- Allow referees to input match data instantly
- Provide live updates for players and spectators

This transforms how padel tournaments are run, making them faster, fairer, and more professional.

---

## Features of a Padel Tournament Scoring App

When choosing a padel tournament management system, these are the key features to look for:

- Live scoring updates during matches  
- Automated tournament brackets and draws  
- Referee-controlled score input  
- Real-time standings and leaderboards  
- Mobile-friendly interface for on-court use  
- Multi-court and multi-tournament support  
- Player and team management system  

---

## Best App for Managing Padel Tournament Draws and Scores

The best padel tournament software should not only track scores but also manage the entire competition flow.

ThinkinCode’s system is designed to:
- Automatically generate tournament draws
- Allow flexible fixture adjustments
- Track match progression in real time
- Reduce admin workload for organisers

This ensures tournaments run smoothly from start to finish.

---

## How to Use a Padel Tournament Scoring App Effectively

To get the most out of a padel scoring system, clubs should:

1. Create teams or player registrations in advance  
2. Generate tournament draws automatically  
3. Assign referees or admins to matches  
4. Input live scores during each match  
5. Allow the system to update standings automatically  

This reduces manual work and improves accuracy across the entire tournament.

---

## Padel Competition Scoring with Live Updates for Spectators

Modern padel tournaments are becoming more interactive.

With live scoring updates:
- Spectators can follow matches in real-time
- Players can track their progress instantly
- Clubs can increase engagement and excitement

This creates a more professional and competitive tournament environment.

---

## Compare Features of Padel Tournament Management Software

When comparing systems, most solutions fall into three categories:

### 1. Manual systems
- Paper scoring
- Spreadsheets
- High error rate
- No live updates

### 2. Generic sports apps
- Not built specifically for padel
- Limited tournament logic
- Poor user experience for clubs

### 3. ThinkinCode padel systems
- Built specifically for padel tournaments
- Live scoring and bracket automation
- Designed for clubs and organisers
- Scalable for multiple tournaments

---

## Best Apps for Scoring Padel Tournaments in South Africa

Padel is growing rapidly in South Africa, and clubs are now looking for better ways to manage tournaments.

A good padel scoring app should:
- Be easy to use for referees
- Work on mobile devices
- Support live scoring
- Handle full tournament structures

ThinkinCode focuses on providing a localised solution tailored for South African padel clubs and complexes.

---

## Where to Download a Reliable Padel Scoring Application

The ThinkinCode padel tournament system is available as a digital platform designed for clubs and organisers.

Clubs can request access, demo the system, and onboard their tournaments directly through ThinkinCode.

---

## Cost of Subscription for Padel Tournament Software in South Africa

Padel tournament software is typically offered in two models:

- Monthly subscription for unlimited tournaments  
- Pay-per-tournament usage model  

ThinkinCode supports flexible pricing depending on club size and usage needs, allowing both small and large venues to benefit from digital tournament management.

---

## Conclusion

Padel tournaments are evolving, and manual systems are no longer efficient enough to manage growing demand.

A digital padel tournament scoring and management system helps clubs:
- Reduce admin work
- Improve accuracy
- Enhance player experience
- Deliver real-time engagement

ThinkinCode is building modern, scalable systems to support the future of padel tournament management in South Africa.`,
      outcomes: [
        "Streamlined tournament organisation",
        "Improved player experience",
        "Enabled real-time match tracking",
        "Positioned clubs with a more professional system",
        "Scalable for multiple venues"
      ],
      tags: ["Sports Tech", "SaaS"],
      screenshot: "/works-covers/PadelEngine Cover.webp",
      ctaType: "view-app",
      ctaLink: "https://padelengine.thinkincode.co.za"
    },
    {
      name: "ThinkinCode x Tennis",
      headline: "Bridging technology and tennis to improve league structure and player engagement",
      description: "There was a need to bridge technology with tennis league experiences to improve engagement and structure. A platform was developed to integrate technology into tennis environments, enabling league fixture management, results tracking, and a structured experience for players and organisers.",
      outcomes: [
        "Improved player engagement",
        "Enhanced digital interaction",
        "Structured experience for users",
        "Scalable concept for sports tech",
        "League fixture and results management"
      ],
      tags: ["Sports Tech"],
      screenshot: "/works-covers/ThinkinCode Meets Tennis Cover.webp",
      ctaType: "view-app",
      ctaLink: "/#contact"
    }
  ],
  everydayApps: [
    {
      name: "Rafeeq App",
      headline: "A quiet, distraction-free companion for building consistent worship habits",
      description: "Many Muslims struggle to maintain consistency in their worship due to a lack of structure, visibility, and calm tracking tools. Existing apps are often cluttered, distracting, or built around engagement rather than sincerity. Rafeeq was built as a quiet, distraction-free digital companion designed to help users organise and track their worship with clarity and intention. The platform allows users to manage prayers (including Qadha), fasts, dhikr, and Qur'an reading through a clean, minimal interface. Designed as a Progressive Web App, Rafeeq works seamlessly across devices while remaining lightweight, fast, and installable — without compromising on simplicity or privacy.",
      outcomes: [
        "Enabled users to build consistent worship habits through structured tracking",
        "Provided a private, distraction-free environment focused on sincerity over engagement",
        "Simplified management of Qadha prayers, fasts, and Qur'an goals",
        "Delivered a fast, installable app experience across mobile and desktop",
        "Created a calm, purpose-driven tool used without ads, noise, or social pressure"
      ],
      tags: ["Mobile App", "PWA", "Productivity", "Faith-Based"],
      screenshot: "/rafeeq/pomelli-image.png",
      carouselImages: [
        "/rafeeq/pomelli-image.png",
        "/rafeeq/pomelli-image-2.png",
        "/rafeeq/pomelli-image-3.png",
        "/rafeeq/pomelli-image-4.png",
        "/rafeeq/pomelli-image-5.png",
        "/rafeeq/pomelli-image-6.png"
      ],
      ctaType: "view-app",
      ctaLink: "https://rafeeq.thinkincode.co.za/",
      storeLinks: {
        appStore: "https://apps.apple.com/za/app/rafeeq/id6767578525",
        playStore: "https://play.google.com/store/apps/details?id=com.thinkincode.rafeeq",
      },
    }
  ],
  tools: [
    {
      name: "Report Generators",
      headline: "Automated report generation tools that eliminate manual formatting and save time",
      description: "Businesses required quick generation of structured reports in formats such as PDF and Excel. Tools were developed to automate report creation and formatting, reducing manual effort and ensuring consistency across documents.",
      outcomes: [
        "Reduced manual reporting time",
        "Improved accuracy",
        "Faster document generation",
        "Increased efficiency"
      ],
      tags: ["Automation", "Tools"],
      screenshot: "/works-covers/Report Generators Cover.webp",
      ctaType: "request-demo",
      ctaLink: "/#contact"
    },
    {
      name: "Conversion Tools",
      headline: "Simple, reliable utilities for common data format conversions",
      description: "Users needed simple utilities to convert data formats quickly. A suite of conversion tools was built to handle common data transformations efficiently and reliably.",
      outcomes: [
        "Simplified data conversion",
        "Improved workflow efficiency",
        "Reduced manual effort",
        "Fast processing"
      ],
      tags: ["Tools"],
      screenshot: "/works-covers/Conversion Tools Cover.webp",
      ctaType: "request-demo",
      ctaLink: "/#contact"
    },
    {
      name: "Enterprise Courier Tracking",
      headline: "Delivery visibility built for an enterprise-scale operation",
      description: "An enterprise-scale business required a simple system to monitor deliveries and provide visibility into their systems. A tracking tool was developed to manage and display delivery statuses, improving operational oversight and customer transparency.",
      outcomes: [
        "Improved delivery visibility",
        "Simplified tracking process",
        "Better operational oversight",
        "Increased efficiency"
      ],
      tags: ["Logistics", "Tool"],
      screenshot: "/works-covers/Ram Couriers Delivery Tracking Tool Cover.webp",
      ctaType: "request-demo",
      ctaLink: "/#contact"
    }
  ]
};

// Client list
export const clientsList = [
  "SSS Cellular",
  "S4 Techno",
  "Taj Printers",
  "UtilityHut",
  "SNK Showroom",
  "StreetGym",
  "Trendsetter Carwash",
  "BrightMindsLearn",
  "Bonsai Paper And Office Supplies",
  "Ministry Of Fun",
  "OffCourt",
  "Zaabs",
  "M-Power"
];

// Category display names and default images for carousel
const categoryMeta = {
  websites: { title: 'Websites', image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80' },
  'business-solutions': { title: 'Business Solutions', image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80' },
  'sports-platforms': { title: 'Sports Platforms', image: 'https://images.unsplash.com/photo-1461896836934-afe7ec8164a1?w=800&q=80' },
  'everyday-apps': { title: 'Everyday Apps', image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=800&q=80' },
  tools: { title: 'Tools', image: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=800&q=80' },
};

// Section configuration
export const sectionsConfig = [
  {
    id: "websites",
    title: "Websites",
    intro: "Professional web presence that builds brand credibility, drives lead generation, and optimizes conversion. Each website is crafted to reflect brand values while delivering measurable business results through strategic design and performance optimization."
  },
  {
    id: "business-solutions",
    title: "Business Solution Products",
    intro: "Operational systems, SaaS platforms, and automation solutions that improve efficiency, increase revenue, and scale with business growth. These platforms provide complete operational control through integrated workflows, real-time analytics, and scalable infrastructure."
  },
  {
    id: "sports-platforms",
    title: "Sports Platforms",
    intro: "Specialized digital systems for sports organizations, managing leagues, tournaments, and player engagement. These platforms automate administrative tasks while improving participant experience through live scoring, statistics, and seamless event management."
  },
  {
    id: "everyday-apps",
    title: "Apps for Everyday Use",
    intro: "Utility applications that combine simplicity with powerful functionality. Built with clean UX principles and performance optimization, these apps deliver value through intuitive design and reliable operation."
  },
  {
    id: "tools",
    title: "Tools Created",
    intro: "Internal engineering tools and utilities that demonstrate technical depth and problem-solving capability. These tools support both internal operations and client needs, showcasing the engineering expertise behind ThinkinCode's solutions."
  }
];

/**
 * Create device mockup - Safari browser for desktop, iPhone for mobile
 */
function createDeviceMockup(type = 'desktop', project = null) {
  if (type === 'mobile') {
    // Magic UI iPhone mockup - exact implementation
    const container = document.createElement('div');
    container.className = 'iphone-mockup';
    
    // Magic UI constants
    const PHONE_WIDTH = 433;
    const PHONE_HEIGHT = 882;
    const SCREEN_X = 21.25;
    const SCREEN_Y = 19.25;
    const SCREEN_WIDTH = 389.5;
    const SCREEN_HEIGHT = 843.5;
    const SCREEN_RADIUS = 55.75;
    
    // Calculated percentages
    const LEFT_PCT = (SCREEN_X / PHONE_WIDTH) * 100;
    const TOP_PCT = (SCREEN_Y / PHONE_HEIGHT) * 100;
    const WIDTH_PCT = (SCREEN_WIDTH / PHONE_WIDTH) * 100;
    const HEIGHT_PCT = (SCREEN_HEIGHT / PHONE_HEIGHT) * 100;
    const RADIUS_H = (SCREEN_RADIUS / SCREEN_WIDTH) * 100;
    const RADIUS_V = (SCREEN_RADIUS / SCREEN_HEIGHT) * 100;
    
    // Get URL from project (src prop equivalent)
    const src = project?.screenshot || project?.ctaLink || '';
    const hasMedia = !!src;
    
    // Create screenshot iframe if URL is provided
    let mediaHTML = '';
    if (src) {
      // Use iframe to show live app (if allowed by site)
      mediaHTML = `
        <div class="iphone-screen-content" style="left: ${LEFT_PCT}%; top: ${TOP_PCT}%; width: ${WIDTH_PCT}%; height: ${HEIGHT_PCT}%; border-radius: ${RADIUS_H}% / ${RADIUS_V}%;">
          <iframe src="${src}" class="iphone-screenshot-iframe" frameborder="0" scrolling="no" loading="lazy"></iframe>
        </div>
      `;
    }
    
    // Generate unique mask ID for this instance
    const maskId = `screenPunch-${Math.random().toString(36).substr(2, 9)}`;
    
    container.innerHTML = `
      <div class="iphone-mockup-wrapper">
        ${mediaHTML}
        <svg viewBox="0 0 ${PHONE_WIDTH} ${PHONE_HEIGHT}" fill="none" xmlns="http://www.w3.org/2000/svg" class="iphone-svg">
          <g ${hasMedia ? `mask="url(#${maskId})"` : ''}>
            <path d="M2 73C2 32.6832 34.6832 0 75 0H357C397.317 0 430 32.6832 430 73V809C430 849.317 397.317 882 357 882H75C34.6832 882 2 849.317 2 809V73Z" class="iphone-frame-outer"/>
            <path d="M0 171C0 170.448 0.447715 170 1 170H3V204H1C0.447715 204 0 203.552 0 203V171Z" class="iphone-frame-outer"/>
            <path d="M1 234C1 233.448 1.44772 233 2 233H3.5V300H2C1.44772 300 1 299.552 1 299V234Z" class="iphone-frame-outer"/>
            <path d="M1 319C1 318.448 1.44772 318 2 318H3.5V385H2C1.44772 385 1 384.552 1 384V319Z" class="iphone-frame-outer"/>
            <path d="M430 279H432C432.552 279 433 279.448 433 280V384C433 384.552 432.552 385 432 385H430V279Z" class="iphone-frame-outer"/>
            <path d="M6 74C6 35.3401 37.3401 4 76 4H356C394.66 4 426 35.3401 426 74V808C426 846.66 394.66 878 356 878H76C37.3401 878 6 846.66 6 808V74Z" class="iphone-frame-inner"/>
          </g>
          
          <path opacity="0.5" d="M174 5H258V5.5C258 6.60457 257.105 7.5 256 7.5H176C174.895 7.5 174 6.60457 174 5.5V5Z" class="iphone-frame-outer"/>
          
          <path d="M${SCREEN_X} 75C${SCREEN_X} 44.2101 46.2101 ${SCREEN_Y} 77 ${SCREEN_Y}H355C385.79 ${SCREEN_Y} 410.75 44.2101 410.75 75V807C410.75 837.79 385.79 862.75 355 862.75H77C46.2101 862.75 ${SCREEN_X} 837.79 ${SCREEN_X} 807V75Z" class="iphone-screen-border" ${hasMedia ? `mask="url(#${maskId})"` : ''}/>
          
          <path d="M154 48.5C154 38.2827 162.283 30 172.5 30H259.5C269.717 30 278 38.2827 278 48.5C278 58.7173 269.717 67 259.5 67H172.5C162.283 67 154 58.7173 154 48.5Z" class="iphone-notch-outer"/>
          <path d="M249 48.5C249 42.701 253.701 38 259.5 38C265.299 38 270 42.701 270 48.5C270 54.299 265.299 59 259.5 59C253.701 59 249 54.299 249 48.5Z" class="iphone-notch-outer"/>
          <path d="M254 48.5C254 45.4624 256.462 43 259.5 43C262.538 43 265 45.4624 265 48.5C265 51.5376 262.538 54 259.5 54C256.462 54 254 51.5376 254 48.5Z" class="iphone-notch-inner"/>
          
          <defs>
            <mask id="${maskId}" maskUnits="userSpaceOnUse">
              <rect x="0" y="0" width="${PHONE_WIDTH}" height="${PHONE_HEIGHT}" fill="white"/>
              <rect x="${SCREEN_X}" y="${SCREEN_Y}" width="${SCREEN_WIDTH}" height="${SCREEN_HEIGHT}" rx="${SCREEN_RADIUS}" ry="${SCREEN_RADIUS}" fill="black"/>
            </mask>
          </defs>
        </svg>
      </div>
    `;
    return container;
  } else {
    // Safari browser mockup (Magic UI style)
    const container = document.createElement('div');
    container.className = 'safari-mockup';
    
    // Get URL from project or use default
    const url = project?.screenshot || project?.ctaLink || 'thinkincode.co.za';
    const displayUrl = url.replace(/^https?:\/\//, '').replace(/\/$/, '');
    
    // Create screenshot iframe or image if URL is provided
    let screenshotHTML = '';
    if (project?.screenshot) {
      const screenshotUrl = project.screenshot;
      // Use iframe to show live website (if allowed by site)
      screenshotHTML = `<iframe src="${screenshotUrl}" class="safari-screenshot-iframe" frameborder="0" scrolling="no" loading="lazy"></iframe>`;
    }
    
    container.innerHTML = `
      <div class="safari-mockup-container">
        <div class="safari-mockup-header">
          <div class="safari-mockup-controls">
            <div class="safari-control-dot safari-control-dot-red"></div>
            <div class="safari-control-dot safari-control-dot-yellow"></div>
            <div class="safari-control-dot safari-control-dot-green"></div>
          </div>
          <div class="safari-mockup-url-bar">
            <div class="safari-url-bar-content">
              <svg class="safari-lock-icon" width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6 1C4.34315 1 3 2.34315 3 4V5H2C1.44772 5 1 5.44772 1 6V10C1 10.5523 1.44772 11 2 11H10C10.5523 11 11 10.5523 11 10V6C11 5.44772 10.5523 5 10 5H9V4C9 2.34315 7.65685 1 6 1ZM6 2C7.10457 2 8 2.89543 8 4V5H4V4C4 2.89543 4.89543 2 6 2ZM2 6H10V10H2V6Z" fill="currentColor"/>
              </svg>
              <span class="safari-url-text">${displayUrl}</span>
            </div>
          </div>
        </div>
        <div class="safari-mockup-content">
          <div class="safari-mockup-screen">
            ${screenshotHTML}
            <div class="safari-mockup-placeholder" ${screenshotHTML ? 'style="display:none;"' : ''}></div>
          </div>
        </div>
      </div>
    `;
    return container;
  }
}

/**
 * Create project card HTML
 */
function createProjectCard(project, sectionId = '') {
  const card = document.createElement('div');
  card.className = 'project-card';
  card.setAttribute('data-animate', 'project-card');
  
  // Use iPhone mockup for: Mobile Apps, Sports Platforms, Business Solutions, and Everyday Apps
  // Use Safari mockup for: Websites and Tools
  const mobileSections = ['business-solutions', 'sports-platforms', 'everyday-apps'];
  const isMobile = project.tags.some(tag => ['Mobile App', 'Sports'].includes(tag)) ||
                   mobileSections.includes(sectionId);
  const deviceType = isMobile ? 'mobile' : 'desktop';
  const mockup = createDeviceMockup(deviceType, project);
  
  let ctaText = 'View App';
  if (project.ctaType === 'request-demo') {
    ctaText = 'Request Demo';
  } else if (project.ctaType === 'view-case-study') {
    ctaText = 'View Case Study';
  }
  
  const tagsHTML = project.tags.map(tag => `<span class="project-tag">${tag}</span>`).join('');
  
  const outcomesHTML = project.outcomes.map(outcome => `<li>${outcome}</li>`).join('');
  
  // Create card structure
  const cardInner = document.createElement('div');
  cardInner.className = 'project-card-inner';
  
  const mockupContainer = document.createElement('div');
  mockupContainer.className = 'project-mockup-container';
  mockupContainer.appendChild(mockup);
  
  const content = document.createElement('div');
  content.className = 'project-content';
  content.innerHTML = `
    <h3 class="project-name">${project.name}</h3>
    <p class="project-headline">${project.headline}</p>
    <div class="project-description">
      <p>${project.description}</p>
    </div>
    <ul class="project-outcomes">
      ${outcomesHTML}
    </ul>
    <div class="project-tags">
      ${tagsHTML}
    </div>
    <a href="${project.ctaLink}" class="project-cta">${ctaText}</a>
  `;
  
  cardInner.appendChild(mockupContainer);
  cardInner.appendChild(content);
  card.appendChild(cardInner);
  
  return card;
}

/**
 * Render projects section
 */
function renderProjectsSection(sectionId, title, intro, projects) {
  const container = document.getElementById('works-projects-container');
  if (!container) return;
  
  const section = document.createElement('div');
  section.className = 'works-section-category';
  section.id = `works-${sectionId}`;
  
  section.innerHTML = `
    <div class="works-category-header">
      <h2 class="works-category-title" data-animate="works-category-title">${title}</h2>
      <p class="works-category-intro" data-animate="works-category-intro">${intro}</p>
    </div>
    <div class="works-projects-grid" data-animate="works-projects-grid">
      <!-- Projects will be inserted here -->
    </div>
  `;
  
  const grid = section.querySelector('.works-projects-grid');
  projects.forEach(project => {
    // Pass sectionId to determine device type
    const card = createProjectCard(project, sectionId);
    grid.appendChild(card);
  });
  
  container.appendChild(section);
}

/**
 * Render client list
 */
function renderClientList() {
  // Check for works page client list
  const worksContainer = document.getElementById('works-clients-grid');
  // Check for main page client list
  const mainContainer = document.getElementById('clients-grid');
  
  const container = worksContainer || mainContainer;
  if (!container) return;
  
  clientsList.forEach(client => {
    const clientItem = document.createElement('div');
    clientItem.className = worksContainer ? 'works-client-item' : 'client-item';
    clientItem.textContent = client;
    container.appendChild(clientItem);
  });
}

const dataKeyMap = {
  'websites': 'websites',
  'business-solutions': 'businessSolutions',
  'sports-platforms': 'sportsPlatforms',
  'everyday-apps': 'everydayApps',
  'tools': 'tools',
};

const defaultImage = 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80';

/**
 * Get cards by category for bento grids
 */
function getCardsByCategory() {
  const byCategory = [];
  sectionsConfig.forEach(section => {
    const dataKey = dataKeyMap[section.id] || section.id;
    const meta = categoryMeta[section.id] || { title: section.title, image: defaultImage };
    const projects = projectsData[dataKey] || [];
    const cards = projects.map((project) => {
      const isImageUrl = project.screenshot && /\.(jpg|jpeg|png|gif|webp)(\?|$)/i.test(project.screenshot);
      const src = isImageUrl ? project.screenshot : (meta.image || defaultImage);
      return {
        category: meta.title,
        title: project.name,
        headline: project.headline,
        src,
        project,
        carouselImages: project.carouselImages || null,
      };
    });
    if (cards.length) {
      byCategory.push({
        id: section.id,
        title: section.title,
        intro: section.intro,
        cards,
      });
    }
  });
  return byCategory;
}

/**
 * Lazy-load Works grid images (real deferral)
 * Replaces `data-src` -> `src` when near viewport.
 */
function initWorksImageLazyLoad() {
  const imgs = document.querySelectorAll('img[data-src][data-lazy="works"]');
  if (!imgs.length) return;

  const loadImg = (img) => {
    const src = img.getAttribute('data-src');
    if (!src) return;
    img.src = src;
    img.removeAttribute('data-src');
    img.removeAttribute('data-lazy');
  };

  if (!('IntersectionObserver' in window)) {
    imgs.forEach(loadImg);
    return;
  }

  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const img = entry.target;
        loadImg(img);
        io.unobserve(img);
      });
    },
    { rootMargin: '500px 0px' }
  );

  imgs.forEach((img) => io.observe(img));
}

const ANIMATED_CARDS_DATA = [
  {
    title: 'Websites',
    description: 'Professional web presence that builds credibility and drives leads.',
    vizTitle: 'Brand presence & lead gen',
    vizDesc: 'Sites that convert visitors into customers.',
    badgeA: 'Leads',
    badgeB: 'Conversions',
    mainColor: '#0f766e',
    secondaryColor: '#5eead4',
    href: '/#contact',
    cta: 'Get a quote',
  },
  {
    title: 'SaaS & Platforms',
    description: 'Scalable systems that power operations and grow with your business.',
    vizTitle: 'Operational scale',
    vizDesc: 'Platforms that grow as your business grows.',
    badgeA: 'Uptime',
    badgeB: 'Automation',
    mainColor: '#7c3aed',
    secondaryColor: '#a78bfa',
    href: '/#contact',
    cta: 'Discuss your platform',
  },
  {
    title: 'Sports & Events',
    description: 'Tournaments, leagues, and scoring - built for the way you play.',
    vizTitle: 'Events & participation',
    vizDesc: 'Leagues, brackets, and live scoring.',
    badgeA: 'Teams',
    badgeB: 'Fixtures',
    mainColor: '#dc2626',
    secondaryColor: '#f87171',
    href: '/#contact',
    cta: 'See sports solutions',
  },
  {
    title: 'Apps & Tools',
    description: 'Custom utilities that simplify workflows and boost productivity.',
    vizTitle: 'Workflow & productivity',
    vizDesc: 'Tools that cut clutter and save time.',
    badgeA: 'Time saved',
    badgeB: 'Accuracy',
    mainColor: '#ea580c',
    secondaryColor: '#fdba74',
    href: '/#contact',
    cta: 'Explore tools',
  },
];

/**
 * Animated “What we build” cards: SVG helpers + HTML builders (shared by works + landing)
 */
const worksLayer1Svg = (main, sec) => `<svg viewBox="0 0 712 180" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8 178C8 176.343 9.34315 175 11 175H25C26.6569 175 28 176.343 28 178V196H8V178Z" fill="${main}"/><path d="M32 168C32 166.343 33.3431 165 35 165H49C50.6569 165 52 166.343 52 168V196H32V168Z" fill="${sec}"/><path d="M67 173C67 171.343 68.3431 170 70 170H84C85.6569 170 87 171.343 87 173V196H67V173Z" fill="${main}"/><path d="M91 153C91 151.343 92.3431 150 94 150H108C109.657 150 111 151.343 111 153V196H91V153Z" fill="${sec}"/><path d="M126 142C126 140.343 127.343 139 129 139H143C144.657 139 146 140.343 146 142V196H126V142Z" fill="${main}"/><path d="M150 158C150 156.343 151.343 155 153 155H167C168.657 155 170 156.343 170 158V196H150V158Z" fill="${sec}"/><path d="M187 133C187 131.343 188.343 130 190 130H204C205.657 130 207 131.343 207 133V196H187V133Z" fill="${main}"/><path d="M211 161C211 159.343 212.343 158 214 158H228C229.657 158 231 159.343 231 161V196H211V161Z" fill="${sec}"/><path d="M248 150C248 148.343 249.343 147 251 147H265C266.657 147 268 148.343 268 150V196H248V150Z" fill="${main}"/><path d="M272 130C272 128.343 273.343 127 275 127H289C290.657 127 292 128.343 292 130V196H272V130Z" fill="${sec}"/><path d="M307 133C307 131.343 308.343 130 310 130H324C325.657 130 327 131.343 327 133V196H307V133Z" fill="${main}"/><path d="M331 155C331 153.343 332.343 152 334 152H348C349.657 152 351 153.343 351 155V196H331V155Z" fill="${sec}"/><path d="M363 161C363 159.343 364.343 158 366 158H380C381.657 158 383 159.343 383 161V196H363V161Z" fill="${main}"/><path d="M387 144C387 142.343 388.343 141 390 141H404C405.657 141 407 142.343 407 144V196H387V144Z" fill="${sec}"/><path d="M423 126C423 124.343 424.343 123 426 123H440C441.657 123 443 124.343 443 126V196H423V126Z" fill="${main}"/><path d="M447 142C447 140.343 448.343 139 450 139H464C465.657 139 467 140.343 467 142V196H447V142Z" fill="${sec}"/><path d="M483 125.461C483 124.102 484.343 123 486 123H500C501.657 123 503 124.102 503 125.461V196H483V125.461Z" fill="${main}"/><path d="M507 137.507C507 136.122 508.343 135 510 135H524C525.657 135 527 136.122 527 137.507V196H507V137.507Z" fill="${sec}"/><path d="M543 108.212C543 106.438 544.343 105 546 105H560C561.657 105 563 106.438 563 108.212V196H543V108.212Z" fill="${main}"/><path d="M567 116.485C567 115.112 568.343 114 570 114H584C585.657 114 587 115.112 587 116.485V196H567V116.485Z" fill="${sec}"/><path d="M603 79.8333C603 78.2685 604.343 77 606 77H620C621.657 77 623 78.2685 623 79.8333V196H603V79.8333Z" fill="${main}"/><path d="M627 91.8919C627 90.2947 628.343 89 630 89H644C645.657 89 647 90.2947 647 91.8919V196H627V91.8919Z" fill="${sec}"/><path d="M661 66.7887C661 65.2485 662.343 64 664 64H678C679.657 64 681 65.2485 681 66.7887V196H661V66.7887Z" fill="${main}"/><path d="M685 55.7325C685 54.2233 686.343 53 688 53H702C703.657 53 705 54.2233 705 55.7325V196H685V55.7325Z" fill="${sec}"/></svg>`;
const worksLayer2Svg = (color, id) => `<svg viewBox="0 0 356 180" fill="none" xmlns="http://www.w3.org/2000/svg"><defs><clipPath id="clip-l2-${id}"><rect width="356" height="180" fill="white"/></clipPath></defs><g clip-path="url(#clip-l2-${id})"><path d="M1 131.5L33.5 125.5L64 102.5L93.5 118.5L124.5 90L154 100.5L183.5 76L207.5 92L244.5 51L274.5 60.5L307.5 46L334.5 28.5L356.5 1" stroke="${color}"/><path d="M33.5 125.5L1 131.5V197H356.5V1L335 28.5L306.5 46L274.5 60.5L244.5 51L207.5 92L183.5 76L154 100.5L124.5 90L93.5 118.5L64 102.5L33.5 125.5Z" fill="${color}" fill-opacity="0.3"/></g></svg>`;
const worksEllipseSvg = (color, id) => `<svg viewBox="0 0 356 180" fill="none"><defs><radialGradient id="ellipse-${id}" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(178 98) rotate(90) scale(98 178)"><stop stop-color="${color}" stop-opacity="0.25"/><stop offset="0.34" stop-color="${color}" stop-opacity="0.15"/><stop offset="1" stop-opacity="0"/></radialGradient></defs><rect width="356" height="180" fill="url(#ellipse-${id})"/></svg>`;

function buildAnimatedCardsSectionHtml() {
  return `
    <section class="works-animated-cards-section">
      <header class="works-usecase-header">
        <p class="works-usecase-heading">What We Build</p>
        <h2 class="works-usecase-title">Digital solutions that scale with your ambition.</h2>
      </header>
      <div class="animated-cards-row">
        ${ANIMATED_CARDS_DATA.map(
          (card, idx) => `
          <a href="${escapeAttr(card.href)}" class="animated-card" style="--main-color: ${card.mainColor}; --secondary-color: ${card.secondaryColor}; --grid-color: ${card.mainColor}15;">
            <div class="animated-card-visual" aria-hidden="true">
              <div class="visual-layer1">${worksLayer1Svg(card.mainColor, card.secondaryColor)}</div>
              <div class="visual-layer2">
                ${worksLayer2Svg(card.mainColor, idx)}
                <div class="visual-layer2-overlay"></div>
              </div>
              <div class="visual-layer3">
                <span class="visual-badge"><span class="visual-badge-dot" style="background:${card.mainColor}"></span>${escapeHtml(card.badgeA || 'Metric')}</span>
                <span class="visual-badge"><span class="visual-badge-dot" style="background:${card.secondaryColor}"></span>${escapeHtml(card.badgeB || 'Metric')}</span>
              </div>
              <div class="visual-layer4">
                <div class="visual-layer4-inner">
                  <div class="visual-layer4-card">
                    <p class="visual-layer4-title">${escapeHtml(card.vizTitle)}</p>
                    <p class="visual-layer4-desc">${escapeHtml(card.vizDesc)}</p>
                  </div>
                </div>
              </div>
              <div class="visual-ellipse-gradient">${worksEllipseSvg(card.mainColor, idx)}</div>
              <div class="visual-grid-layer"></div>
            </div>
            <div class="animated-card-body">
              <h3 class="animated-card-title">${escapeHtml(card.title)}</h3>
              <p class="animated-card-description">${escapeHtml(card.description)}</p>
              <span class="animated-card-cta">${escapeHtml(card.cta)} <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 17L17 7M17 7H7M17 7V17"/></svg></span>
            </div>
          </a>
        `
        ).join('')}
      </div>
    </section>
  `;
}

function buildCaseStudiesAndCtaHtml(categories) {
  let html = '';

  categories.forEach((cat) => {
    const cards = cat.cards
      .map((card, i) => `
      <article class="expandable-card-trigger works-editorial-card ${i % 3 === 0 ? 'is-featured' : ''}" data-card data-project-idx="${escapeAttr(JSON.stringify({ catId: cat.id, idx: i }))}">
        <div class="expandable-card-preview">
          <div class="expandable-card-image">
            <img data-lazy="works" data-src="${escapeAttr(card.src)}" src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==" alt="${escapeAttr(card.title)}" loading="lazy" decoding="async" />
          </div>
          <div class="expandable-card-header">
            <h3 class="expandable-card-title">${escapeHtml(card.title)}</h3>
            <p class="expandable-card-desc">${escapeHtml(card.headline)}</p>
            <span class="expandable-card-link">Learn more <svg class="expandable-card-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 9l6 6 6-6"/></svg></span>
          </div>
        </div>
      </article>
    `)
      .join('');

    const tagline = cat.intro.split('.')[0] + '.';
    html += `
      <section class="works-usecase-section" data-category="${escapeAttr(cat.id)}">
        <div class="works-editorial-layout">
          <header class="works-usecase-header works-sticky-header">
            <p class="works-usecase-heading">Case Studies</p>
            <h2 class="works-usecase-title">${escapeHtml(cat.title)}</h2>
            <p class="works-usecase-intro">${escapeHtml(tagline)}</p>
          </header>
          <div class="works-usecase-grid">
            ${cards}
          </div>
        </div>
      </section>
    `;
  });

  html += `
    <section class="works-cta-section">
      <div class="works-cta-inner">
        <h2 class="works-cta-heading">Your next project could be here.</h2>
        <p class="works-cta-sub">Ready to turn your vision into reality? Let's create something remarkable together.</p>
        <div class="works-cta-buttons">
          <a href="/#contact" class="works-cta works-cta-primary">Start Your Project</a>
          <a href="https://wa.me/27665733031" class="works-cta works-cta-outline" target="_blank" rel="noopener noreferrer">Quick chat</a>
        </div>
      </div>
    </section>
  `;

  return html;
}

function renderBentoCarousel() {
  const container = document.getElementById('works-projects-container');
  if (!container) return;
  const categories = getCardsByCategory();
  if (categories.length === 0) return;
  container.innerHTML = buildAnimatedCardsSectionHtml() + buildCaseStudiesAndCtaHtml(categories);
  initExpandableCardModal();
  initWorksImageLazyLoad();
}

export function mountLandingWorksSplit(whatWeBuildEl, showcaseEl) {
  const categories = getCardsByCategory();
  if (!whatWeBuildEl || !showcaseEl || categories.length === 0) return;
  whatWeBuildEl.innerHTML = buildAnimatedCardsSectionHtml();
  showcaseEl.innerHTML = buildCaseStudiesAndCtaHtml(categories);
  initExpandableCardModal();
  initWorksImageLazyLoad();
  initAnimatedCardsScrollReveal();
  initEditorialCardsReveal();
}
const carouselPrevSvg = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="24" height="24"><path d="M15 18l-6-6 6-6"/></svg>';
const carouselNextSvg = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="24" height="24"><path d="M9 18l6-6-6-6"/></svg>';

let worksModalSavedScrollY = 0;

/**
 * Lock page scroll so Lenis / touch do not move the background; only the modal panel scrolls.
 */
function setWorksModalScrollLock(modal, locked) {
  const lenis = window.lenis;
  if (locked) {
    worksModalSavedScrollY = window.scrollY || document.documentElement.scrollTop || 0;
    if (lenis && typeof lenis.stop === 'function') lenis.stop();
    document.documentElement.classList.add('works-modal-open');
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${worksModalSavedScrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
  } else {
    document.documentElement.classList.remove('works-modal-open');
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    window.scrollTo(0, worksModalSavedScrollY);
    if (lenis) {
      if (typeof lenis.scrollTo === 'function') {
        lenis.scrollTo(worksModalSavedScrollY, { immediate: true });
      }
      if (typeof lenis.start === 'function') lenis.start();
    }
  }
}

/**
 * Open expandable card modal (Badtz UI style popup)
 */
function openExpandableCardModal(card) {
  const modal = document.getElementById('expandable-card-modal');
  const body = document.getElementById('expandable-card-modal-body');
  if (!modal || !body || !card) return;

  const p = card.project;
  const blogCtas =
    p.blogMarkdown
      ? `
        <div class="expandable-card-blog-ctas">
          <a href="/#contact" class="expandable-card-blog-cta primary">Request a demo</a>
          <a href="/#contact" class="expandable-card-blog-cta">Pricing now</a>
        </div>`
      : '';
  const blogHtml = p.blogMarkdown ? `<hr />${renderMarkdownLite(p.blogMarkdown)}${blogCtas}` : '';
  const storeBadgesHtml = renderStoreBadgesHtml(p);
  const ctaHtml =
    p.ctaLink && p.ctaLink !== '#'
      ? `<a href="${escapeAttr(p.ctaLink)}" class="expandable-card-cta" target="_blank" rel="noopener noreferrer">View Project <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 17L17 7M17 7H7M17 7V17"/></svg></a>`
      : '';

  const images = card.carouselImages && card.carouselImages.length > 0 ? card.carouselImages : [card.src];
  const isCarousel = images.length > 1;

  const imageBlock = isCarousel
    ? `
    <div class="expandable-card-modal-carousel">
      <div class="expandable-card-modal-image expandable-card-modal-carousel-track">
        ${images.map((url, i) => `<img src="${escapeAttr(url)}" alt="" class="expandable-card-modal-carousel-slide" data-index="${i}" ${i === 0 ? '' : 'style="display:none"'}>`).join('')}
      </div>
      <button type="button" class="expandable-card-modal-carousel-prev" aria-label="Previous image">${carouselPrevSvg}</button>
      <button type="button" class="expandable-card-modal-carousel-next" aria-label="Next image">${carouselNextSvg}</button>
      <div class="expandable-card-modal-carousel-dots">${images.map((_, i) => `<button type="button" class="expandable-card-modal-carousel-dot ${i === 0 ? 'is-active' : ''}" data-index="${i}" aria-label="Go to image ${i + 1}"></button>`).join('')}</div>
    </div>`
    : `
    <div class="expandable-card-modal-image">
      <img src="${escapeAttr(card.src)}" alt="" />
    </div>`;

  const scrollDownSvg = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 5v14M19 12l-7 7-7-7"/></svg>';
  body.innerHTML = `
    ${imageBlock}
    <button type="button" class="expandable-card-modal-scroll-down" aria-label="Scroll down to content">
      <span class="expandable-card-modal-scroll-down-text">Scroll down</span>
      ${scrollDownSvg}
    </button>
    <div class="expandable-card-modal-body">
      <h2 class="expandable-card-modal-title">${escapeHtml(card.title)}</h2>
      <p class="expandable-card-modal-desc">${escapeHtml(card.headline)}</p>
      <div class="expandable-card-modal-content">
        <p>${escapeHtml(p.description)}</p>
        <h4>Key outcomes</h4>
        <ul>${p.outcomes.map((o) => `<li>${escapeHtml(o)}</li>`).join('')}</ul>
        <div class="expandable-card-modal-tags">${p.tags.map((t) => `<span class="expandable-card-tag">${escapeHtml(t)}</span>`).join('')}</div>
        ${storeBadgesHtml}
        ${blogHtml}
        ${ctaHtml}
      </div>
    </div>
  `;

  modal.classList.add('is-open');
  modal.setAttribute('aria-hidden', 'false');
  setWorksModalScrollLock(modal, true);

  const panel = modal.querySelector('.expandable-card-modal-panel');
  if (panel) {
    panel.scrollTop = 0;
    requestAnimationFrame(() => panel.focus({ preventScroll: true }));
  }

  if (isCarousel) initModalCarousel(body.querySelector('.expandable-card-modal-carousel'));

  const scrollDownBtn = body.querySelector('.expandable-card-modal-scroll-down');
  if (scrollDownBtn) {
    scrollDownBtn.addEventListener('click', () => {
      const panel = body.closest('.expandable-card-modal-panel');
      const target = body.querySelector('.expandable-card-modal-body');
      if (panel && target) panel.scrollTo({ top: target.offsetTop - 16, behavior: 'smooth' });
    });
  }
}

let modalCarouselInterval = null;

function initModalCarousel(carouselEl) {
  if (!carouselEl) return;
  if (modalCarouselInterval) clearInterval(modalCarouselInterval);
  const slides = carouselEl.querySelectorAll('.expandable-card-modal-carousel-slide');
  const dots = carouselEl.querySelectorAll('.expandable-card-modal-carousel-dot');
  const prevBtn = carouselEl.querySelector('.expandable-card-modal-carousel-prev');
  const nextBtn = carouselEl.querySelector('.expandable-card-modal-carousel-next');
  let index = 0;
  const total = slides.length;

  function goTo(i) {
    index = ((i % total) + total) % total;
    slides.forEach((s, j) => { s.style.display = j === index ? 'block' : 'none'; });
    dots.forEach((d, j) => d.classList.toggle('is-active', j === index));
  }

  prevBtn?.addEventListener('click', () => goTo(index - 1));
  nextBtn?.addEventListener('click', () => goTo(index + 1));
  dots.forEach((d) => d.addEventListener('click', () => goTo(parseInt(d.getAttribute('data-index'), 10))));

  modalCarouselInterval = setInterval(() => goTo(index + 1), 3000);
}

function closeExpandableCardModal() {
  if (modalCarouselInterval) {
    clearInterval(modalCarouselInterval);
    modalCarouselInterval = null;
  }
  const modal = document.getElementById('expandable-card-modal');
  if (!modal) return;
  modal.classList.remove('is-open');
  modal.setAttribute('aria-hidden', 'true');
  setWorksModalScrollLock(modal, false);
}

/**
 * Initialize expandable card modal (click card → popup)
 */
let worksExpandableModalDocListenersBound = false;

function initExpandableCardModal() {
  const cards = document.querySelectorAll('.expandable-card-trigger[data-project-idx]');
  const modal = document.getElementById('expandable-card-modal');
  const closeBtn = modal?.querySelector('.expandable-card-modal-close');
  const backdrop = modal?.querySelector('.expandable-card-modal-backdrop');

  const categories = getCardsByCategory();
  const categoryMap = new Map(categories.map((c) => [c.id, c]));

  cards.forEach((el) => {
    el.addEventListener('click', () => {
      try {
        const { catId, idx } = JSON.parse(el.getAttribute('data-project-idx') || '{}');
        const cat = categoryMap.get(catId);
        const card = cat?.cards[idx];
        if (card) openExpandableCardModal(card);
      } catch (_) {}
    });
  });

  const close = () => closeExpandableCardModal();
  if (modal && !modal.dataset.expandableModalUiBound) {
    modal.dataset.expandableModalUiBound = '1';
    closeBtn?.addEventListener('click', close);
    backdrop?.addEventListener('click', close);
  }

  if (!worksExpandableModalDocListenersBound) {
    worksExpandableModalDocListenersBound = true;
    document.addEventListener('keydown', (e) => {
      const m = document.getElementById('expandable-card-modal');
      if (e.key === 'Escape' && m?.classList.contains('is-open')) closeExpandableCardModal();
    });

    const onWheelCapture = (e) => {
      const m = document.getElementById('expandable-card-modal');
      if (!m?.classList.contains('is-open')) return;
      const panel = m.querySelector('.expandable-card-modal-panel');
      if (panel && panel.contains(e.target)) return;
      e.preventDefault();
    };

    document.addEventListener('wheel', onWheelCapture, { passive: false, capture: true });
  }
}

function escapeHtml(s) {
  if (!s) return '';
  const div = document.createElement('div');
  div.textContent = s;
  return div.innerHTML;
}

function escapeAttr(s) {
  if (!s) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

const APP_STORE_BADGE_SVG = `<svg class="store-badge-icon" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>`;

const PLAY_STORE_BADGE_SVG = `<svg class="store-badge-icon" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor"><path d="M3.609 1.814 13.792 12 3.61 22.186a1.004 1.004 0 0 1-.61-.92V2.734a1.004 1.004 0 0 1 .609-.92zm10.89 10.893 2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198 2.807 1.626a1.004 1.004 0 0 1 0 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658 16.8 8.99l-2.302 2.302-8.634-8.634z"/></svg>`;

function renderStoreBadgesHtml(project) {
  const links = project?.storeLinks;
  if (!links || (!links.appStore && !links.playStore)) return '';

  const badges = [];

  if (links.appStore) {
    badges.push(`
      <a href="${escapeAttr(links.appStore)}" class="store-badge store-badge--apple" target="_blank" rel="noopener noreferrer" aria-label="Download Rafeeq on the App Store">
        ${APP_STORE_BADGE_SVG}
        <span class="store-badge-text">
          <span class="store-badge-kicker">Download on the</span>
          <span class="store-badge-title">App Store</span>
        </span>
      </a>`);
  }

  if (links.playStore) {
    badges.push(`
      <a href="${escapeAttr(links.playStore)}" class="store-badge store-badge--google" target="_blank" rel="noopener noreferrer" aria-label="Get Rafeeq on Google Play">
        ${PLAY_STORE_BADGE_SVG}
        <span class="store-badge-text">
          <span class="store-badge-kicker">Get it on</span>
          <span class="store-badge-title">Google Play</span>
        </span>
      </a>`);
  }

  return `<div class="expandable-card-store-badges">${badges.join('')}</div>`;
}

function renderMarkdownLite(md) {
  const lines = String(md || '').replace(/\r\n/g, '\n').split('\n');
  let html = '';
  let inUl = false;
  let inOl = false;

  const closeLists = () => {
    if (inUl) {
      html += '</ul>';
      inUl = false;
    }
    if (inOl) {
      html += '</ol>';
      inOl = false;
    }
  };

  const inline = (s) => {
    const safe = escapeHtml(s);
    return safe.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  };

  for (const rawLine of lines) {
    const line = rawLine.trimEnd();
    const trimmed = line.trim();

    if (trimmed === '') {
      closeLists();
      continue;
    }

    if (/^---+$/.test(trimmed)) {
      closeLists();
      html += '<hr />';
      continue;
    }

    if (trimmed.startsWith('# ')) {
      closeLists();
      html += `<h3>${inline(trimmed.slice(2))}</h3>`;
      continue;
    }

    if (trimmed.startsWith('## ')) {
      closeLists();
      html += `<h4>${inline(trimmed.slice(3))}</h4>`;
      continue;
    }

    if (/^###\s+/.test(trimmed)) {
      closeLists();
      html += `<h4>${inline(trimmed.replace(/^###\s+/, ''))}</h4>`;
      continue;
    }

    const olMatch = trimmed.match(/^(\d+)\.\s+(.+)$/);
    if (olMatch) {
      if (inUl) closeLists();
      if (!inOl) {
        html += '<ol>';
        inOl = true;
      }
      html += `<li>${inline(olMatch[2])}</li>`;
      continue;
    }

    if (trimmed.startsWith('- ')) {
      if (inOl) closeLists();
      if (!inUl) {
        html += '<ul>';
        inUl = true;
      }
      html += `<li>${inline(trimmed.slice(2))}</li>`;
      continue;
    }

    closeLists();
    html += `<p>${inline(trimmed)}</p>`;
  }

  closeLists();
  return html;
}

/**
 * On touch devices, trigger animated card "hover" state when card scrolls into view
 */
function initAnimatedCardsScrollReveal() {
  if (!window.matchMedia('(hover: none)').matches) return;

  const cards = document.querySelectorAll('.animated-card');
  if (cards.length === 0) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        entry.target.classList.toggle('is-in-view', entry.isIntersecting);
      });
    },
    { rootMargin: '0px 0px -10% 0px', threshold: 0.2 }
  );

  cards.forEach((card) => observer.observe(card));
}

function initEditorialCardsReveal() {
  const cards = document.querySelectorAll('.works-editorial-card');
  if (cards.length === 0) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) entry.target.classList.add('is-visible');
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.15 }
  );

  cards.forEach((card) => observer.observe(card));
}

/**
 * Initialize works section
 */
export function initWorks() {
  renderBentoCarousel();
  initAnimatedCardsScrollReveal();
  initEditorialCardsReveal();
}

/**
 * Initialize client list (for main page only)
 */
export function initClients() {
  renderClientList();
}
